import { useCallback, useEffect, useState } from "react";
import Modal from "modules/Core/sub-modules/ui-kit/components/Modal";
import Container from "modules/Core/sub-modules/ui-kit/components/Container";
import Title from "modules/Core/sub-modules/ui-kit/components/Title";
import Text from "modules/Core/sub-modules/ui-kit/components/Text";
import Button from "modules/Core/sub-modules/ui-kit/components/Button";
import { useIsOpen } from "modules/Core/hooks/useIsOpen";
import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import withFocusedBoothId from "modules/booths/higher-order-components/withFocusedBoothId";
import useOnSuccess from "modules/Core/sub-modules/Dialog/hooks/useOnSuccess";
import useOnError from "modules/Core/sub-modules/Dialog/hooks/useOnError";
import cells from "modules/cycles/state";
import middleware from "modules/cycles/middleware";
import { firstPlaceholderActivity } from "modules/cycles/consts";
import styles from "./styles.module.scss";

const CycleOption = ({ cycle, badge, onSelect, selecting }) => (
    <Container className={styles.cycleItem} flex spaceBetween alignCenter>
        <Container flex col className={styles.cycleCopy}>
            {badge && <Text className={styles.badge}>{badge}</Text>}
            <Title Element="h4" className={styles.cycleName}>
                {cycle?.name || "Cycle"}
            </Title>
        </Container>
        <Button
            nature="ocean-blue"
            loading={selecting}
            onClick={() => onSelect(cycle)}
        >
            Select
        </Button>
    </Container>
);

const AllocateActivityButton = ({
    activityId,
    allocated,
    activeCycle,
    boothId,
    assign,
    fetchActive,
    onAllocated,
    text = "Add to cycle",
}) => {
    const { isOpen, open, close } = useIsOpen();
    const success = useOnSuccess();
    const error = useOnError();
    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [selectingId, setSelectingId] = useState(null);

    const allocateToCycle = useCallback((cycle, callback) => {
        const placeholder = firstPlaceholderActivity(cycle);
        if (!cycle?.id || !placeholder?.id) {
            error("That cycle has no empty slot");
            return;
        }
        setSelectingId(cycle.id);
        assign({
            cycleId: cycle.id,
            placeholderId: placeholder.id,
            activityId,
            callback: (res) => {
                setSelectingId(null);
                if (!res?.id) return error("Unable to add this task to that cycle");
                success("Task added to cycle");
                close();
                onAllocated?.();
                callback?.(res);
            },
        });
    }, [activityId, assign, close, error, onAllocated, success]);

    const handleClick = useCallback(async () => {
        if (!activityId || allocated) return;
        let cycle = activeCycle;
        if (!cycle?.id && boothId) {
            const result = await middleware.ops.fetchCurrentcycle({ boothId });
            cycle = result?.currentCycle;
            if (boothId) fetchActive?.(boothId);
        }
        const placeholder = firstPlaceholderActivity(cycle);
        if (placeholder?.id && cycle?.id) {
            allocateToCycle(cycle);
            return;
        }
        open();
    }, [activeCycle, activityId, allocated, allocateToCycle, boothId, fetchActive, open]);

    useEffect(() => {
        if (!isOpen || !boothId) return;
        let cancelled = false;
        const load = async () => {
            setLoadingOptions(true);
            try {
                const [currentResult, upcomingResult] = await Promise.all([
                    middleware.ops.fetchCurrentcycle({ boothId }),
                    middleware.ops.fetchFeed({ boothId, isForthcoming: true }),
                ]);
                const next = [];
                const current = currentResult?.currentCycle;
                if (current?.id && firstPlaceholderActivity(current)) {
                    next.push({ ...current, badge: "Active" });
                }
                const seen = new Set(next.map((item) => item.id));
                for (const cycle of upcomingResult?.feed?.entities ?? []) {
                    if (!cycle?.id || seen.has(cycle.id)) continue;
                    if (!firstPlaceholderActivity(cycle)) continue;
                    next.push({ ...cycle, badge: "Upcoming" });
                    seen.add(cycle.id);
                }
                if (!cancelled) setOptions(next);
            } finally {
                if (!cancelled) setLoadingOptions(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [boothId, isOpen]);

    if (allocated) {
        return (
            <Button disabled>
                In a cycle
            </Button>
        );
    }

    return (
        <>
            <Button onClick={handleClick}>
                {text}
            </Button>
            <Modal isOpen={isOpen} onClose={close}>
                <Container className={styles.modal} flex col>
                    <Title className={styles.title}>Add to a cycle</Title>
                    <Text className={styles.hint}>
                        Choose a cycle with an empty slot. This task will take the first one.
                    </Text>
                    {loadingOptions ? (
                        <Text className={styles.hint}>Loading cycles…</Text>
                    ) : options.length === 0 ? (
                        <Text className={styles.empty}>
                            No cycle has an empty slot right now. Free a slot, or start a new cycle from Tasks.
                        </Text>
                    ) : (
                        <Container className={styles.list} flex col>
                            {options.map((cycle) => (
                                <CycleOption
                                    key={cycle.id}
                                    cycle={cycle}
                                    badge={cycle.badge}
                                    selecting={selectingId === cycle.id}
                                    onSelect={(selected) => allocateToCycle(selected)}
                                />
                            ))}
                        </Container>
                    )}
                </Container>
            </Modal>
        </>
    );
};

export default withFocusedBoothId(strappedConnected(
    AllocateActivityButton,
    {
        activeCycle: cells.fetchCurrentCycle.selectors.activeCycle,
    },
    {
        assign: cells.assignActivityToCycle.action,
        fetchActive: (boothId) => cells.fetchCurrentCycle.action({ boothId }),
    },
    ({ activityId, allocated, activeCycle, boothId, assign, fetchActive, onAllocated, text }) => ({
        activityId,
        allocated,
        activeCycle,
        boothId,
        assign,
        fetchActive,
        onAllocated,
        text,
    })
));
